import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight,
  BadgeCheck,
  Briefcase,
  Building2,
  ChevronLeft,
  Clock,
  MapPin,
  Sparkles,
  UserPlus,
  Users,
} from 'lucide-react';
import Layout from '../../components/layout/layout';
import AuthModal from '../../components/auth/auth-modal';
import { fetchParcoursTeaserBySlug, fetchParcoursBySlug } from '../../lib/parcours';
import { captureInviteFromUrl } from '../../lib/parcoursInvite';
import { useAuth } from '../../AuthProvider';
import { supabase } from '../../lib/supabase';
import type { Parcours } from '../../types/parcours';
import { CONTRACT_TYPE_LABELS, REMOTE_TYPE_LABELS } from '../../types/offres';

type RelatedOffer = {
  id: string;
  slug: string;
  title: string;
  contract_type: string;
  location_city: string;
  remote_type: string;
  salary_range: string | null;
  description: string | null;
  company_name: string | null;
  company_logo_url: string | null;
  image_url: string | null;
  published_at: string | null;
};

function stripHtml(str: string | null | undefined): string {
  if (!str) return '';
  return str.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim().slice(0, 150);
}

export default function ParcoursLandingPage() {
  const { companySlug } = useParams<{ companySlug: string }>();
  const { user, loading: authLoading } = useAuth();

  const [parcours, setParcours] = useState<Parcours | null>(null);
  const [relatedOffers, setRelatedOffers] = useState<RelatedOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);

  // Capture le token d'invitation ATS (?invite=) — utilisé par l'étape
  // finale "À vous de jouer" pour pré-remplir le formulaire.
  useEffect(() => {
    if (companySlug) captureInviteFromUrl(companySlug);
  }, [companySlug]);

  useEffect(() => {
    if (authLoading || !companySlug) {
      if (!companySlug) { setNotFound(true); setLoading(false); }
      return;
    }

    let cancelled = false;

    async function loadPage() {
      try {
        // Use full fetch (with steps) if authenticated, teaser if not
        const data = user
          ? await fetchParcoursBySlug(companySlug)
          : await fetchParcoursTeaserBySlug(companySlug);

        if (cancelled) return;
        if (!data) { setNotFound(true); return; }
        setParcours(data);

        // Load related offers (public data, no auth required)
        try {
          const { data: offers, error } = await supabase
            .from('job_offers')
            .select('id, slug, title, contract_type, location_city, remote_type, salary_range, description, company_name, company_logo_url, image_url, published_at')
            .eq('status', 'published')
            .eq('parcours_id', data.id)
            .order('published_at', { ascending: false, nullsFirst: false })
            .order('created_at', { ascending: false });

          if (!cancelled && !error && offers) {
            setRelatedOffers(offers as RelatedOffer[]);
            if (import.meta.env.DEV && offers.length === 0) {
              console.warn('Aucune offre publiée liée à ce parcours via parcours_id.', { parcours_id: data.id });
            }
          }
          if (error) console.warn('Offres liées non chargées', error);
        } catch (e) {
          console.warn('Erreur chargement offres liées', e);
        }
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadPage();
    return () => { cancelled = true; };
  }, [companySlug, user, authLoading]);

  const firstStep = parcours?.steps?.[0] ?? null;

  const title = parcours
    ? parcours.meta_title ?? `${parcours.company_name} — Parcours de découverte | recruteurs.io`
    : 'Parcours de découverte | recruteurs.io';

  const description = parcours
    ? parcours.meta_description ?? parcours.description ?? parcours.tagline ?? ''
    : '';

  if (authLoading || loading) {
    return (
      <Layout>
        <Helmet>
          <title>Chargement... | recruteurs.io</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="min-h-screen bg-[#f7f7f8]">
          <div className="container-custom max-w-6xl py-24">
            <div className="animate-pulse space-y-8">
              <div className="h-[360px] rounded-[2rem] bg-neutral-200" />
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
                <div className="h-64 rounded-3xl bg-white" />
                <div className="h-80 rounded-3xl bg-white" />
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (notFound || !parcours) {
    return (
      <Layout>
        <Helmet>
          <title>Parcours introuvable | recruteurs.io</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="container-custom max-w-5xl py-24 text-center">
          <p className="text-5xl font-bold text-neutral-200 mb-4">404</p>
          <h1 className="text-2xl font-semibold text-neutral-800 mb-2">Parcours introuvable</h1>
          <p className="text-neutral-500 mb-8">Ce parcours n'existe pas ou n'est plus disponible.</p>
          <Link
            to="/decouvrir"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#d03484] hover:text-[#d47254] transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Voir tous les parcours
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>{title}</title>
        {description && <meta name="description" content={description} />}
        <link rel="canonical" href={`https://recruteurs.io/decouvrir/${companySlug}/`} />
        <meta name="robots" content="index, follow" />
        {parcours.og_image && <meta property="og:image" content={parcours.og_image} />}
      </Helmet>

      <AuthModal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)} />

      <main className="bg-[#f7f7f8]">
        {/* Top back link */}
        <div className="bg-white border-b border-neutral-100">
          <div className="container-custom max-w-6xl py-4">
            <Link
              to="/decouvrir"
              className="inline-flex items-center gap-2 text-sm font-medium text-neutral-500 hover:text-neutral-900 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Retour aux parcours
            </Link>
          </div>
        </div>

        {/* Hero — compact (≈50 % de l'ancienne hauteur, laisse voir le début de la section en dessous) */}
        <section className="relative min-h-[240px] md:min-h-[280px] overflow-hidden bg-neutral-950">
          {parcours.company_cover ? (
            <img
              src={parcours.company_cover}
              alt={`Découvrir ${parcours.company_name}`}
              className="absolute inset-0 h-full w-full object-cover"
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-[#d03484] via-[#d47254] to-neutral-950" />
          )}
          <div className="absolute inset-0 bg-neutral-950/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-950/50 via-transparent to-transparent" />

          <div className="relative z-10 container-custom max-w-6xl min-h-[240px] md:min-h-[280px] flex items-center justify-center text-center py-10 md:py-12">
            <div className="max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#d03484] to-[#d47254] px-3 py-1 text-[11px] font-bold whitespace-nowrap uppercase tracking-wide text-white shadow-lg mb-3">
                <Sparkles className="w-3.5 h-3.5" />
                {parcours.sector ?? 'Découverte entreprise'}
              </div>
              {parcours.company_logo && (
                <div className="mx-auto mb-3 h-12 w-12 md:h-14 md:w-14 rounded-2xl bg-white p-2 shadow-xl ring-1 ring-white/30">
                  <img
                    src={parcours.company_logo}
                    alt={`Logo ${parcours.company_name}`}
                    className="h-full w-full object-contain"
                    loading="eager"
                    decoding="async"
                  />
                </div>
              )}
              <h1 className="text-2xl md:text-4xl font-bold tracking-tight text-white leading-[1.1] mb-2">
                Parcours de découverte
                <span className="block">{parcours.company_name}</span>
              </h1>
              {parcours.tagline && (
                <p className="text-sm md:text-base text-white/85 leading-relaxed max-w-2xl mx-auto">
                  {parcours.tagline}
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Body */}
        <section className="relative z-20 -mt-10 pb-20">
          <div className="container-custom max-w-6xl">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 items-start">

              {/* Colonne principale */}
              <div className="space-y-8">

                {/* Intro card */}
                <div className="rounded-[1.75rem] bg-white p-7 md:p-10 shadow-sm ring-1 ring-neutral-200">
                  <div className="inline-flex items-center gap-2 rounded-full bg-[#d03484]/10 px-4 py-2 text-sm font-semibold text-[#d03484] mb-6">
                    <BadgeCheck className="w-4 h-4" />
                    Bienvenue dans votre parcours de découverte
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-neutral-950 mb-5">
                    Découvrez l'entreprise avant de candidater.
                  </h2>
                  <div className="space-y-4 text-neutral-700 leading-relaxed">
                    {parcours.description ? (
                      <p className="whitespace-pre-line">{parcours.description}</p>
                    ) : (
                      <p>
                        Ce parcours a été conçu pour vous permettre de comprendre l'entreprise,
                        sa culture, son environnement, ses attentes et les opportunités qui s'offrent à vous.
                      </p>
                    )}
                    <p>
                      Prenez le temps de parcourir chaque étape. L'objectif est simple :
                      vous aider à vous projeter avec les bonnes informations avant de candidater.
                    </p>
                  </div>

                  {(parcours.location || parcours.employee_count || parcours.sector) && (
                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {parcours.location && (
                        <div className="rounded-2xl bg-[#f7f7f8] p-4 ring-1 ring-neutral-200">
                          <MapPin className="w-5 h-5 text-[#d03484] mb-2" />
                          <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400 mb-1">Localisation</p>
                          <p className="font-semibold text-neutral-900">{parcours.location}</p>
                        </div>
                      )}
                      {parcours.employee_count && (
                        <div className="rounded-2xl bg-[#f7f7f8] p-4 ring-1 ring-neutral-200">
                          <Users className="w-5 h-5 text-[#d03484] mb-2" />
                          <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400 mb-1">Équipe</p>
                          <p className="font-semibold text-neutral-900">{parcours.employee_count}</p>
                        </div>
                      )}
                      {parcours.sector && (
                        <div className="rounded-2xl bg-[#f7f7f8] p-4 ring-1 ring-neutral-200">
                          <Building2 className="w-5 h-5 text-[#d03484] mb-2" />
                          <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400 mb-1">Secteur</p>
                          <p className="font-semibold text-neutral-900">{parcours.sector}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Offres liées */}
                <div>
                  <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                    <div>
                      <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-neutral-600 shadow-sm ring-1 ring-neutral-200 mb-4">
                        <Briefcase className="w-4 h-4 text-[#d03484]" />
                        Offres ouvertes
                      </div>
                      <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-neutral-950">
                        Les offres ouvertes chez{' '}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
                          {parcours.company_name}
                        </span>
                      </h2>
                      <p className="text-neutral-600 mt-2 text-sm">
                        Découvrez l'entreprise, puis candidatez en connaissance de cause.
                      </p>
                    </div>
                    <Link
                      to="/offres"
                      className="inline-flex items-center gap-2 text-sm font-semibold text-[#d03484] hover:text-[#d47254] transition-colors shrink-0"
                    >
                      Voir toutes les offres
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>

                  {relatedOffers.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      {relatedOffers.map((offer) => {
                        const excerpt = stripHtml(offer.description);
                        return (
                          <article
                            key={offer.id}
                            className="group rounded-[1.5rem] bg-white p-6 shadow-sm ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-xl transition-all duration-300 flex flex-col"
                          >
                            <div className="flex items-start justify-between gap-4 mb-4">
                              <div className="flex-1 min-w-0">
                                <h3 className="text-lg font-bold text-neutral-950 group-hover:text-[#d03484] transition-colors leading-snug">
                                  {offer.title}
                                </h3>
                                {(() => {
                                  const displayName = (!offer.company_name || offer.company_name.trim().toLowerCase() === 'confidentiel')
                                    ? parcours.company_name
                                    : offer.company_name;
                                  return displayName ? (
                                    <p className="text-sm text-neutral-500 mt-0.5">{displayName}</p>
                                  ) : null;
                                })()}
                              </div>
                              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#d03484] to-[#d47254] flex items-center justify-center text-white shrink-0">
                                <Briefcase className="w-5 h-5" />
                              </div>
                            </div>

                            <div className="flex flex-wrap gap-2 mb-4">
                              {offer.location_city && (
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-3 py-1.5 text-xs font-medium text-neutral-600">
                                  <MapPin className="w-3 h-3" />
                                  {offer.location_city}
                                </span>
                              )}
                              {offer.contract_type && (
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-3 py-1.5 text-xs font-medium text-neutral-600">
                                  {CONTRACT_TYPE_LABELS[offer.contract_type as keyof typeof CONTRACT_TYPE_LABELS] ?? offer.contract_type}
                                </span>
                              )}
                              {offer.remote_type && offer.remote_type !== 'no' && (
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-3 py-1.5 text-xs font-medium text-neutral-600">
                                  <Clock className="w-3 h-3" />
                                  {REMOTE_TYPE_LABELS[offer.remote_type as keyof typeof REMOTE_TYPE_LABELS] ?? offer.remote_type}
                                </span>
                              )}
                              {offer.salary_range && (
                                <span className="inline-flex items-center rounded-full bg-[#d03484]/10 px-3 py-1.5 text-xs font-semibold text-[#d03484]">
                                  {offer.salary_range}
                                </span>
                              )}
                            </div>

                            {excerpt && (
                              <p className="text-sm text-neutral-600 leading-relaxed mb-4 flex-1">
                                {excerpt}{offer.description && offer.description.length > 150 ? '…' : ''}
                              </p>
                            )}

                            <Link
                              to={`/offres/${offer.slug}`}
                              className="mt-auto inline-flex items-center gap-2 text-sm font-bold text-neutral-950 group-hover:text-[#d03484] transition-colors"
                            >
                              Voir l'offre
                              <ArrowRight className="w-4 h-4" />
                            </Link>
                          </article>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="rounded-[1.5rem] bg-white p-8 shadow-sm ring-1 ring-neutral-200 text-center">
                      <Briefcase className="w-8 h-8 text-neutral-300 mx-auto mb-3" />
                      <p className="text-neutral-500 text-sm mb-3">Aucune offre ouverte pour le moment.</p>
                      <Link
                        to="/offres"
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#d03484] hover:text-[#d47254] transition-colors"
                      >
                        Voir toutes les offres
                        <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  )}
                </div>
              </div>

              {/* Sidebar CTA */}
              <aside className="w-full lg:sticky lg:top-24">
                <div className="rounded-[1.75rem] bg-white shadow-xl shadow-neutral-900/10 ring-1 ring-neutral-200 overflow-hidden">
                  <div className="relative h-40 bg-neutral-100">
                    {parcours.company_cover ? (
                      <img
                        src={parcours.company_cover}
                        alt=""
                        className="h-full w-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div className="h-full w-full bg-gradient-to-br from-[#d03484] to-[#d47254]" />
                    )}
                    {parcours.company_logo && (
                      <div className="absolute -bottom-8 left-6 h-16 w-16 rounded-2xl bg-white p-2 shadow-lg ring-1 ring-neutral-200">
                        <img
                          src={parcours.company_logo}
                          alt={`Logo ${parcours.company_name}`}
                          className="h-full w-full object-contain"
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                    )}
                  </div>

                  <div className="p-6 pt-12">
                    <p className="text-sm font-semibold text-neutral-500 mb-1">{parcours.company_name}</p>
                    <h3 className="text-2xl font-bold text-neutral-950 mb-3">Prêt à commencer ?</h3>
                    <p className="text-sm text-neutral-600 leading-relaxed mb-6">
                      {user
                        ? 'Découvrez les étapes clés du parcours avant de candidater.'
                        : 'Créez un compte gratuitement pour accéder au parcours complet.'}
                    </p>

                    {user ? (
                      firstStep ? (
                        <Link
                          to={`/decouvrir/${parcours.slug}/etape/${firstStep.step_id}`}
                          className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-5 py-2 text-sm font-bold whitespace-nowrap text-white shadow-lg shadow-[#d03484]/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
                        >
                          Découvrir l'entreprise
                          <ArrowRight className="w-4 h-4" />
                        </Link>
                      ) : (
                        <p className="text-center text-sm text-neutral-400 italic">Parcours bientôt disponible.</p>
                      )
                    ) : (
                      <div className="space-y-3">
                        <button
                          onClick={() => setAuthModalOpen(true)}
                          className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-5 py-2 text-sm font-bold whitespace-nowrap text-white shadow-lg shadow-[#d03484]/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
                        >
                          Se connecter pour démarrer
                          <ArrowRight className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setAuthModalOpen(true)}
                          className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-neutral-200 px-5 py-2 text-sm font-semibold whitespace-nowrap text-neutral-700 hover:border-neutral-300 hover:bg-neutral-50 transition-all"
                        >
                          <UserPlus className="w-4 h-4" />
                          Créer un compte candidat
                        </button>
                      </div>
                    )}

                    {relatedOffers.length > 0 && (
                      <p className="mt-4 text-center text-xs text-neutral-400">
                        {relatedOffers.length} offre{relatedOffers.length > 1 ? 's' : ''} ouverte{relatedOffers.length > 1 ? 's' : ''} chez {parcours.company_name}
                      </p>
                    )}
                  </div>
                </div>
              </aside>

            </div>
          </div>
        </section>
      </main>
    </Layout>
  );
}
